﻿@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.service

import org.example.project.dashboard.ScreenManager
import org.example.project.manager.NodeScope

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlin.js.Date

object GlobalManager {
    var currentFirmenId: String = ""
        private set

    fun setFirmenId(k: String) {
        currentFirmenId = k
    }

    fun setNodeIdentity(firmenId: String) {
        currentFirmenId = firmenId
    }

    private val _sessionConfigVersion = mutableStateOf(0)
    private val _screenVersions = mutableStateMapOf<String, Long>()
    private val _jsonTopicVersions = mutableStateMapOf<String, Long>()
    private val _jsonPathVersions = mutableStateMapOf<String, Long>()
    private val _jsonDomainVersions = mutableStateMapOf<String, Long>()
    private val mountedScreens = mutableStateListOf<String>()
    private val pendingScreenVersions = mutableMapOf<String, Long>()
    private val localCommitRefreshSuppressedUntil = mutableMapOf<String, Long>()
    private var pendingScreenRefreshTimer: Int? = null

    fun sessionConfigVersion(): Int = _sessionConfigVersion.value

    fun triggerSessionConfigReload() {
        _sessionConfigVersion.value++
    }

    fun screenVersion(screenKey: String): Long = _screenVersions[screenKey] ?: 0L

    fun jsonTopicVersion(topic: String): Long = _jsonTopicVersions[normalizeJsonKey(topic)] ?: 0L

    fun jsonPathVersion(relativePath: String): Long = _jsonPathVersions[normalizeJsonKey(relativePath)] ?: 0L

    fun jsonDomainVersion(domain: String): Long = _jsonDomainVersions[normalizeJsonKey(domain)] ?: 0L

    fun markLocalScreenWrite(screenKey: String, suppressMs: Long = 1500L) {
        val cleanKey = screenKey.trim()
        if (cleanKey.isBlank()) return
        localCommitRefreshSuppressedUntil[cleanKey] = Date.now().toLong() + suppressMs.coerceAtLeast(1500L)
        pendingScreenVersions.remove(cleanKey)
    }

    fun markLocalTabularCommit(screenKey: String) {
        markLocalScreenWrite(screenKey)
    }

    fun markJsonChanged(topic: String, relativePath: String, changedAt: String = "") {
        val version = parseJsonVersion(changedAt)
        val cleanTopic = normalizeJsonKey(topic)
        val cleanPath = normalizeJsonKey(relativePath)
        if (cleanTopic.isNotBlank()) _jsonTopicVersions[cleanTopic] = version
        if (cleanPath.isNotBlank()) _jsonPathVersions[cleanPath] = version
        jsonChangeResources(cleanTopic, cleanPath).forEach { resource ->
            _jsonDomainVersions[normalizeJsonKey(resource)] = version
        }
        val resources = jsonChangeResources(cleanTopic, cleanPath)
        if (!usesFineGrainedJsonRefresh(resources)) {
            queueMountedScreensForJsonChange(cleanTopic, cleanPath, version)
        }
        if (requiresSessionConfigReload(cleanTopic, cleanPath)) {
            triggerSessionConfigReload()
        }
    }

    fun mountScreen(screenKey: String) {
        if (screenKey.isBlank() || screenKey in mountedScreens) return
        mountedScreens.add(screenKey)
    }

    fun unmountScreen(screenKey: String) {
        mountedScreens.remove(screenKey)
    }

    private fun normalizeJsonKey(value: String): String =
        value.trim().replace('\\', '/').lowercase()

    private fun parseJsonVersion(changedAt: String): Long =
        Date.parse(changedAt).takeIf { !it.isNaN() }?.toLong() ?: Date.now().toLong()

    private fun queueMountedScreensForJsonChange(topic: String, relativePath: String, version: Long) {
        val resources = jsonChangeResources(topic, relativePath)
        if (resources.isEmpty()) return

        mountedScreens.toList().forEach { screenKey ->
            if (resources.any { resource -> ScreenManager.matchesResource(screenKey, resource) }) {
                if (isLocalCommitEchoSuppressed(screenKey, version)) return@forEach
                pendingScreenVersions[screenKey] = maxOf(pendingScreenVersions[screenKey] ?: 0L, version)
            }
        }
        if (pendingScreenVersions.isNotEmpty() && pendingScreenRefreshTimer == null) {
            pendingScreenRefreshTimer = window.setTimeout({
                flushPendingScreenRefreshes()
            }, 180)
        }
    }

    private fun flushPendingScreenRefreshes() {
        pendingScreenRefreshTimer = null
        val pending = pendingScreenVersions.toMap()
        pendingScreenVersions.clear()
        pending.forEach { (screenKey, version) ->
            if (!isLocalCommitEchoSuppressed(screenKey, version)) {
                _screenVersions[screenKey] = version
            }
        }
    }

    private fun isLocalCommitEchoSuppressed(screenKey: String, version: Long): Boolean {
        val suppressedUntil = localCommitRefreshSuppressedUntil[screenKey] ?: return false
        val now = Date.now().toLong()
        return if (now <= suppressedUntil && version <= suppressedUntil) {
            true
        } else {
            localCommitRefreshSuppressedUntil.remove(screenKey)
            false
        }
    }

    private fun usesFineGrainedJsonRefresh(resources: Set<String>): Boolean =
        resources.any {
            it in setOf("urlaub", "support", "cr", "schulung", "kalender")
        }

    private fun jsonChangeResources(topic: String, relativePath: String): Set<String> =
        buildSet {
            if (topic.isNotBlank()) {
                add(topic)
                topic.substringBeforeLast('.', missingDelimiterValue = topic)
                    .takeIf { it.isNotBlank() && it != topic }
                    ?.let(::add)
            }
            if (relativePath.isNotBlank()) {
                add(relativePath)
                relativePath.substringAfterLast('/').takeIf { it.isNotBlank() }?.let { fileName ->
                    add(fileName)
                    fileName.substringBeforeLast('.', missingDelimiterValue = fileName)
                        .takeIf { it.isNotBlank() && it != fileName }
                        ?.let(::add)
                }
                relativePath.substringBefore('/').takeIf { it.isNotBlank() && it != relativePath }?.let(::add)
            }
            derivedDomainsForJsonPath(topic, relativePath).forEach(::add)
        }

    private fun derivedDomainsForJsonPath(topic: String, relativePath: String): Set<String> {
        val cleanTopic = normalizeJsonKey(topic)
        val cleanPath = normalizeJsonKey(relativePath)
        val isUserPath = cleanPath.startsWith("user/") || cleanPath.contains("/user/")
        val hasZeiterfassungPath = cleanPath.startsWith("zeiterfassung/") || cleanPath.contains("/zeiterfassung/")
        return buildSet {
            if (cleanPath.contains("/termine/") || cleanTopic == "kalenderslots.txt") {
                add("kalender")
            }
            if (cleanPath.contains("/vertragsdaten/support/")) {
                add("support")
            }
            if (cleanPath.contains("/vertragsdaten/cr/")) {
                add("cr")
            }
            if (cleanPath.contains("/schulung/")) {
                add("schulung")
            }
            if (isUserPath && hasZeiterfassungPath) {
                add("personal")
                add("zeit")
            }
            if (hasZeiterfassungPath && (cleanTopic == "zeitprofile.json" || cleanPath.endsWith("zeitprofile.json"))) {
                add("personal")
                add("zeit")
            }
            if (isUserPath && cleanPath.contains("/termine/")) {
                add("personal")
                add("kalender")
                add("urlaub")
            }
            if (isUserPath && cleanPath.contains("/abwesenheit/")) {
                add("personal")
                add("kalender")
                add("urlaub")
            }
            if (isUserPath && cleanPath.contains("/kontingente/")) {
                add("personal")
                add("urlaub")
                add("zeit")
            }
            if (isUserPath && cleanPath.contains("/zeitkonto/")) {
                add("personal")
                add("urlaub")
                add("zeit")
            }
            if (isUserPath && cleanPath.contains("/akte/")) {
                add("personal")
            }
            if (isUserPath && cleanPath.contains("/qr/")) {
                add("personal")
                add("user")
            }
            if (cleanPath.startsWith("stempeluhren/") || cleanPath.contains("/stempeluhren/")) {
                add("personal")
                add("zeit")
            }
            if (cleanPath.contains("/dokumente/")) {
                add("service-documents")
            } else if (cleanPath.startsWith("service/snap/") || cleanPath.contains("/service/snap/") ||
                cleanPath.startsWith("snap/") || cleanPath.contains("/snap/") || cleanTopic == "snap"
            ) {
                add("snap")
            } else if (cleanPath.startsWith("register/") || cleanPath.contains("/register/")) {
                add("company")
                if (cleanPath.contains("/mandanten/") || cleanTopic == "mandant.json") {
                    add("mandant")
                }
                if (cleanPath.contains("/snapsettings/")) {
                    add("snap")
                }
                if (cleanPath.contains("/yard/kontrollen/")) {
                    add("yard")
                    add("yard-kontrollen")
                }
                if (cleanPath.contains("/we/")) {
                    add("yard")
                    add("yard-we")
                    if (cleanTopic == "auftrag.json") add("yard-we-auftrag")
                    if (cleanTopic.startsWith("lkw_")) add("yard-we-lkw")
                    if (cleanTopic == "vorgangslog.json") add("yard-vorgangslog")
                }
                if (cleanPath.contains("/wa/")) {
                    add("yard")
                    add("yard-wa")
                    if (cleanTopic.startsWith("auftrag_")) add("yard-wa-auftrag")
                    if (cleanTopic.startsWith("lkw_")) add("yard-wa-lkw")
                    if (cleanTopic.startsWith("standort_")) add("yard-wa-standort")
                    if (cleanTopic == "vorgangslog.json") add("yard-vorgangslog")
                }
                if (cleanPath.contains("/standorte/")) {
                    add("yard")
                    add("yard-infra")
                    if (cleanPath.contains("/parkplatz/")) add("yard-parkplatz")
                    if (cleanTopic.startsWith("tor_")) add("yard-tor")
                }
            } else if (cleanPath.startsWith("firmenstamm/") || cleanPath.contains("/firmenstamm/")) {
                add("firmenstamm")
            } else if (cleanPath.contains("/yard/kontrollen/")) {
                add("yard")
                add("yard-kontrollen")
            } else if (cleanPath.contains("/we/")) {
                add("yard")
                add("yard-we")
                if (cleanTopic == "auftrag.json") add("yard-we-auftrag")
                if (cleanTopic.startsWith("lkw_")) add("yard-we-lkw")
                if (cleanTopic == "vorgangslog.json") add("yard-vorgangslog")
            } else if (cleanPath.contains("/wa/")) {
                add("yard")
                add("yard-wa")
                if (cleanTopic.startsWith("auftrag_")) add("yard-wa-auftrag")
                if (cleanTopic.startsWith("lkw_")) add("yard-wa-lkw")
                if (cleanTopic == "vorgangslog.json") add("yard-vorgangslog")
            } else if (cleanPath.startsWith("standorte/") || cleanPath.contains("/standorte/")) {
                add("yard")
                add("yard-infra")
                if (cleanPath.contains("/parkplatz/")) add("yard-parkplatz")
                if (cleanTopic.startsWith("tor_")) add("yard-tor")
            } else if (cleanPath.contains("/yard/") || cleanTopic == "we_torfreigaben.json") {
                add("yard")
            }
        }
    }

    private fun requiresSessionConfigReload(topic: String, relativePath: String): Boolean {
        val resources = jsonChangeResources(topic, relativePath)
        if (resources.isEmpty()) return false
        return resources.any { resource ->
            resource == "screenconfig.json" ||
                resource == "screenanzeigemodus" ||
                resource == "lizenz" ||
                resource == "lizenz.json" ||
                resource == "module" ||
                resource == "module.json" ||
                resource == "rolle" ||
                resource == "rolle.json" ||
                resource == "rollen" ||
                resource == "dashboardfeeds.json" ||
                resource == "settings.json" ||
                resource == "mandant.json"
        }
    }
}

fun currentSaveArt(): String =
    if (GlobalManager.currentFirmenId.isNotBlank()) NodeScope.CUS else NodeScope.SYS
